<template>
  <article
    class="garage-opinions"
    v-if="garage"
  >
    <div class="garage-opinions__header">
      <h2 class="garage-opinions__total-comments">Opinie o warsztacie ({{ garageTotalCommentsWithGoogleComments }})</h2>
      <a :href="`/mechanicy/dodaj_komentarz/${route.params.garageId}`">
        <DmButton title="Dodaj opinię" />
      </a>
    </div>

    <div
      v-if="allCommentsElements.length"
      class="garage-opinions__rating"
    >
      <div class="garage-opinions__rating-header">
        <div class="d-flex align-items-center justify-content-center">
          <h2>{{ garage.ratingTotal }}</h2>
          <div class="garage-opinions__rating-stars">
            <NuxtImg
              v-for="(item, index) in 6"
              alt="Ocena DobryMechanik.pl"
              width="18px"
              height="18px"
              class="garage-opinions__rating-star"
              :src="getStar(garage.ratingTotal || 0, index).toString()"
            />
          </div>
        </div>
        <p>Ocena wystawiona na podstawie <span>{{ garageTotalComments }}
            {{ getCommentsTranslatePL(garageTotalComments) }}</span></p>
      </div>

      <div class="garage-opinions__rating-rows">
        <div class="garage-opinions__rating-item">
          <p>Ocena ogólna</p>
          <div class="garage-opinions__rating-stars">
            <NuxtImg
              v-for="(item, index) in 6"
              alt="Ocena DobryMechanik.pl"
              width="18px"
              height="18px"
              class="garage-opinions__rating-star"
              :src="getStar(garage.ratingTotal || 0, index).toString()"
            />
          </div>
        </div>

        <div class="garage-opinions__rating-item">
          <p>Czas naprawy</p>
          <div class="garage-opinions__rating-stars">
            <NuxtImg
              width="18px"
              alt="Ocena DobryMechanik.pl"
              height="18px"
              v-for="(item, index) in 6"
              class="garage-opinions__rating-star"
              :src="getStar(garage.ratingTime || 0, index).toString()"
            />
          </div>
        </div>

        <div class="garage-opinions__rating-item">
          <p>Cena usług</p>
          <div class="garage-opinions__rating-stars">
            <NuxtImg
              v-for="(item, index) in 6"
              width="18px"
              alt="Ocena DobryMechanik.pl"
              height="18px"
              class="garage-opinions__rating-star"
              :src="getStar(garage.ratingPrice || 0, index).toString()"
            />
          </div>
        </div>

        <div class="garage-opinions__rating-item">
          <p>Jakość usługi</p>
          <div class="garage-opinions__rating-stars">
            <NuxtImg
              v-for="(item, index) in 6"
              width="18px"
              height="18px"
              alt="Ocena DobryMechanik.pl"
              class="garage-opinions__rating-star"
              :src="getStar(garage.ratingService || 0, index).toString()"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="garage-opinions__no-opinions"
    >
      <h4>Napisz pierwszą opinię</h4>
      <p>
        Korzystałeś z usług tego warsztatu?
        Opowiedz nam o swoich doświadczeniach.
        Pomóż innym kierowcom w znalezieniu dobrego mechanika.
      </p>
    </div>

    <div class="garage-opinions__list">
      <GarageOpinionElement
        :garageOpinion="garageOpinion"
        v-for="garageOpinion in allCommentsElements"
        :key="garageOpinion.id"
      />
      <GarageGoogleOpinion
        v-if="(garageTotalComments || 0) < 10"
        v-for="(commentFromGoogle, index) in commentsFromGoogle"
        :key="`google-opinion-${index}`"
        :garageOpinion="commentFromGoogle"
      />
    </div>

    <button
      v-if="(garageTotalComments || 0) > allCommentsElements.length"
      @click="emits('load-more')"
      class="garage-opinions__show-more"
    >Zobacz więcej</button>
  </article>
</template>

<script setup lang="ts">
import { getStar } from '@/helpers/get-star';
import { computed } from 'vue';
import GarageOpinionElement from './GarageOpinionElement.vue';
import GarageGoogleOpinion from './GarageGoogleOpinion.vue';
import type { GarageCommentJsonldGarageReadIdGarageReadNameCommentReadAnswerRead, GarageJsonldGarageReadGarageReadIdGarageReadNameGarageSlugHistoryReadSuggestedGarageRead, RankingJsonldSuggestedGarageReadRankingRead } from '~/api/docs/api';
import { DmButton } from '@dobrymechanik/vue-ui-v2';
import type { GarageGooglePlaceReview } from '~/api/docs/app';
import { getCommentsTranslatePL } from '@/helpers/helpers';
import GarageAdsenseElastic from './GarageAdsenseElastic.vue';

interface IProps {
  garage?: GarageJsonldGarageReadGarageReadIdGarageReadNameGarageSlugHistoryReadSuggestedGarageRead;
  garageComments: GarageCommentJsonldGarageReadIdGarageReadNameCommentReadAnswerRead[];
  garageTotalComments?: number;
  garageTotalCommentsWithGoogleComments?: number;
  garageOtherComments: GarageCommentJsonldGarageReadIdGarageReadNameCommentReadAnswerRead[];
  commentsFromGoogle: GarageGooglePlaceReview[];
  suggestedGarages?: RankingJsonldSuggestedGarageReadRankingRead[];
}

interface IEmits {
  (name: 'load-more'): void;
}

// use
const route = useRoute();

// emits
const emits = defineEmits<IEmits>();

// props
const props = defineProps<IProps>();

const allCommentsElements: ComputedRef<GarageCommentJsonldGarageReadIdGarageReadNameCommentReadAnswerRead[]> = computed(() => {
  return [...props.garageComments, ...props.garageOtherComments];
})


</script>

<style lang="scss">
@import url('assets/styles/components/garage-opinions.scss');
</style>